import { DateTime } from "luxon";
import { z } from "zod";

export const REASON_CODES = [
  "CONTRACT_CHANGE",
  "APPROVED_EXCEPTION",
  "PRICING_UPDATE",
  "TEMPORARY_CHANGE",
  "CORRECTION",
  "PRODUCT_CHANGE",
  "OTHER",
] as const;

// Sentinel value used in the bundle dropdown to indicate the user wants to
// create a fresh single-use bundle from per-SKU prices instead of selecting
// an existing bundle. The backend swaps this for the real bundle id after
// creating the new PricingBundle row.
export const CUSTOM_PRICING_BUNDLE_ID = "__custom__";

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const MIN_PRICE_IN_MILLICENTS = 10;
const DOLLAR_IN_MILLICENTS = 100 * 1000; // cents * millicents-per-cent

// Per-SKU custom-pricing cap: standard bundle price plus this offset.
export const CUSTOM_PRICE_MAX_OFFSET_MILLICENTS = 250;

export type StandardPricesBySkuId = Record<string, number>;

function formatMillicentsAsDollars(millicents: number): string {
  return `$${millicents / DOLLAR_IN_MILLICENTS}`;
}

function maxPriceForSku(
  skuId: string,
  standardPricesBySkuId: StandardPricesBySkuId
): number | undefined {
  const standard = standardPricesBySkuId[skuId];
  return standard === undefined
    ? undefined
    : standard + CUSTOM_PRICE_MAX_OFFSET_MILLICENTS;
}

const priceInMillicentsSchema = z
  .number({
    required_error: "Required",
    invalid_type_error: "Required",
  })
  .int("Must be a whole number of millicents")
  .min(
    MIN_PRICE_IN_MILLICENTS,
    `Must be at least ${MIN_PRICE_IN_MILLICENTS} millicents (${(
      MIN_PRICE_IN_MILLICENTS / 1000
    ).toFixed(2)} cents)`
  );

function makeCustomPriceSchema(standardPricesBySkuId: StandardPricesBySkuId) {
  return z
    .object({
      skuId: z.string().min(1),
      priceInMillicents: priceInMillicentsSchema,
    })
    .superRefine((entry, ctx) => {
      const max = maxPriceForSku(entry.skuId, standardPricesBySkuId);
      if (max !== undefined && entry.priceInMillicents > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Must be at most ${formatMillicentsAsDollars(max)}`,
          path: ["priceInMillicents"],
        });
      }
    });
}

function makeCustomPriceListField(
  standardPricesBySkuId: StandardPricesBySkuId
) {
  return z.array(makeCustomPriceSchema(standardPricesBySkuId)).nullish();
}

export type ReasonCodeValue = (typeof REASON_CODES)[number];

export const reasonCodeSchema = z.enum(REASON_CODES, {
  errorMap: (issue, ctx) => {
    if (
      issue.code === "invalid_type" &&
      (ctx.data === undefined || ctx.data === null)
    ) {
      return { message: "Required" };
    }
    return { message: `Invalid reason code: ${String(ctx.data)}` };
  },
});

// Accepts a JS Date or a valid Luxon DateTime; outputs a JS Date normalized
// to midnight UTC (any time component is stripped).
const flexDateSchema = z
  .union(
    [
      z.date({ errorMap: () => ({ message: "Required" }) }),
      z
        .custom<DateTime>(
          (val): val is DateTime => val instanceof DateTime && val.isValid
        )
        .transform(dt => dt.toJSDate()),
    ],
    {
      errorMap: () => ({ message: "Required" }),
    }
  )
  .transform(date => {
    const normalized = new Date(date.getTime());
    normalized.setUTCHours(0, 0, 0, 0);
    return normalized;
  });

const nullableFlexDateSchema = flexDateSchema.nullable();

// Preprocess a reason-code field: "" (MUI Select default) becomes null so that
// a union-abort on the empty string does not prevent superRefine from running.
const nullableReasonCodeField = z.preprocess(
  (val): unknown => (val === "" ? null : val),
  reasonCodeSchema.nullable()
);

function makeBasePricingAssignmentFields(
  standardPricesBySkuId: StandardPricesBySkuId
) {
  return {
    initialBundleId: z.string().min(1, "Required"),
    // Use nullable so that an empty string (preprocessed to null) passes base
    // validation and does not abort superRefine.
    initialReasonCode: nullableReasonCodeField,
    initialNotes: z.string().nullish(),
    // Accept null so the form's default null value does not abort superRefine.
    effectiveDate: nullableFlexDateSchema,
    expirationDate: nullableFlexDateSchema.optional(),
    followUpBundleId: z.string().nullish(),
    followUpReasonCode: z.preprocess(
      (val): unknown => (val === "" ? null : val),
      reasonCodeSchema.nullable().optional()
    ),
    followUpNotes: z.string().nullish(),
    initialCustomPrices: makeCustomPriceListField(standardPricesBySkuId),
    followUpCustomPrices: makeCustomPriceListField(standardPricesBySkuId),
  };
}

type CustomPriceValue = { skuId: string; priceInMillicents: number };
type CustomPriceField = CustomPriceValue[] | null | undefined;

function isCustomPricesEmpty(value: CustomPriceField): boolean {
  return value == null || value.length === 0;
}

type PricingAssignmentData = {
  initialBundleId: string;
  initialReasonCode: ReasonCodeValue | null;
  initialNotes?: string | null;
  effectiveDate: Date | null;
  expirationDate?: Date | null;
  followUpBundleId?: string | null;
  followUpReasonCode?: ReasonCodeValue | null;
  followUpNotes?: string | null;
  initialCustomPrices?: CustomPriceField;
  followUpCustomPrices?: CustomPriceField;
  customIsWeText?: boolean | null;
};

function refinePricingAssignment(
  data: PricingAssignmentData,
  ctx: z.RefinementCtx,
  {
    requireFollowUpBundle,
    requireCustomIsWeText,
  }: {
    requireFollowUpBundle: boolean;
    requireCustomIsWeText: boolean;
  }
): void {
  if (!data.initialReasonCode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["initialReasonCode"],
    });
  }
  if (!data.effectiveDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["effectiveDate"],
    });
  }
  if (data.initialReasonCode === "OTHER" && !data.initialNotes?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Notes are required when the change reason is Other",
      path: ["initialNotes"],
    });
  }

  const initialIsCustom = data.initialBundleId === CUSTOM_PRICING_BUNDLE_ID;
  if (initialIsCustom) {
    if (isCustomPricesEmpty(data.initialCustomPrices)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["initialCustomPrices"],
      });
    }
    if (
      requireCustomIsWeText &&
      (data.customIsWeText === null || data.customIsWeText === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["customIsWeText"],
      });
    }
  }

  if (data.expirationDate) {
    if (data.effectiveDate) {
      const oneDayAfter = new Date(
        data.effectiveDate.getTime() + ONE_DAY_IN_MS
      );
      if (data.expirationDate < oneDayAfter) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Expiration date must be at least one day after the effective date",
          path: ["expirationDate"],
        });
      }
    }
    // followUpReasonCode/followUpNotes without a followUpBundleId can occur when
    // the form held stale values that became hidden (e.g. followUpBundleId
    // switched to "None" after a reason was already chosen). Treat the bundle
    // as required whenever any follow-up field carries data.
    const hasFollowUpReasonOrNotes =
      !!data.followUpReasonCode || !!data.followUpNotes?.trim();
    if (
      (requireFollowUpBundle || hasFollowUpReasonOrNotes) &&
      !data.followUpBundleId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpBundleId"],
      });
    } else if (
      data.followUpBundleId &&
      data.followUpBundleId === data.initialBundleId &&
      data.followUpBundleId !== CUSTOM_PRICING_BUNDLE_ID
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "After-expiration bundle must be different from the primary bundle",
        path: ["followUpBundleId"],
      });
    }
    if (data.followUpBundleId === CUSTOM_PRICING_BUNDLE_ID) {
      if (isCustomPricesEmpty(data.followUpCustomPrices)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["followUpCustomPrices"],
        });
      }
    }
    if (data.followUpBundleId && !data.followUpReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpReasonCode"],
      });
    } else if (requireFollowUpBundle && !data.followUpReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpReasonCode"],
      });
    }
    if (data.followUpReasonCode === "OTHER" && !data.followUpNotes?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Notes are required when the change reason is Other",
        path: ["followUpNotes"],
      });
    }
  }
}

// Non-exported "sample" schemas used purely as a `typeof` source for the
// factories' return-type annotation.
const _changeTenantPricingAssignmentSchemaSample = z
  .object({
    ...makeBasePricingAssignmentFields({}),
    customIsWeText: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: true,
      requireCustomIsWeText: true,
    });
  });

const _bulkChangeTenantsPricingAssignmentSchemaSample = z
  .object({
    ...makeBasePricingAssignmentFields({}),
    tenantIds: z.array(z.string().min(1)).min(1, "Required"),
    customIsWeText: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: true,
      requireCustomIsWeText: true,
    });
  });

const _createSubaccountPricingAssignmentsSchemaSample = z
  .object({
    ...makeBasePricingAssignmentFields({}),
    subaccountIds: z.array(z.string().min(1)).min(1, "Required"),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: false,
      requireCustomIsWeText: false,
    });
  });

export function makeChangeTenantPricingAssignmentSchema({
  standardPricesBySkuId,
}: {
  standardPricesBySkuId: StandardPricesBySkuId;
}): typeof _changeTenantPricingAssignmentSchemaSample {
  return z
    .object({
      ...makeBasePricingAssignmentFields(standardPricesBySkuId),
      customIsWeText: z.boolean().nullish(),
    })
    .superRefine((data, ctx) => {
      refinePricingAssignment(data, ctx, {
        requireFollowUpBundle: true,
        requireCustomIsWeText: true,
      });
    });
}

export function makeBulkChangeTenantsPricingAssignmentSchema({
  standardPricesBySkuId,
}: {
  standardPricesBySkuId: StandardPricesBySkuId;
}): typeof _bulkChangeTenantsPricingAssignmentSchemaSample {
  return z
    .object({
      ...makeBasePricingAssignmentFields(standardPricesBySkuId),
      tenantIds: z.array(z.string().min(1)).min(1, "Required"),
      customIsWeText: z.boolean().nullish(),
    })
    .superRefine((data, ctx) => {
      refinePricingAssignment(data, ctx, {
        requireFollowUpBundle: true,
        requireCustomIsWeText: true,
      });
    });
}

export function makeCreateSubaccountPricingAssignmentsSchema({
  standardPricesBySkuId,
}: {
  standardPricesBySkuId: StandardPricesBySkuId;
}): typeof _createSubaccountPricingAssignmentsSchemaSample {
  return z
    .object({
      ...makeBasePricingAssignmentFields(standardPricesBySkuId),
      subaccountIds: z.array(z.string().min(1)).min(1, "Required"),
    })
    .superRefine((data, ctx) => {
      refinePricingAssignment(data, ctx, {
        requireFollowUpBundle: false,
        requireCustomIsWeText: false,
      });
    });
}
